import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { adminLogout } from '@suangua/shared';

/**
 * 管理端布局（M5，PLAN §6.5）
 * - 子路由 = 侧栏菜单（/admin 统计 / users 用户 / posts 帖子 / comments 评论 / reports 举报 / settings 设置）
 * - 视觉与用户端玄墨鎏金轻微呼应（墨色侧栏 + 金色高亮），主体保持浅色高密度管理风格
 * - 前端仅做 UI 层守卫：任意管理接口 401 时跳回 /admin/login；真正边界在服务端 requireAdmin
 */

const MENUS = [
  { to: '/admin', end: true, icon: 'fa-solid fa-chart-line', label: '统计' },
  { to: '/admin/users', icon: 'fa-solid fa-users', label: '用户' },
  { to: '/admin/posts', icon: 'fa-regular fa-file-lines', label: '帖子' },
  { to: '/admin/comments', icon: 'fa-regular fa-comment-dots', label: '评论' },
  { to: '/admin/reports', icon: 'fa-regular fa-flag', label: '举报' },
  { to: '/admin/settings', icon: 'fa-solid fa-gear', label: '设置' },
] as const;

export function AdminLayout() {
  const navigate = useNavigate();
  const [error, setError] = useState('');

  return (
    <div className="flex min-h-screen bg-stone-50">
      {/* 侧栏（墨色 + 金色高亮，与用户端视觉呼应） */}
      <aside className="flex w-48 shrink-0 flex-col bg-stone-900">
        <div className="flex h-14 items-center gap-2 px-4">
          <span className="grid h-7 w-7 -rotate-4 place-items-center rounded-lg bg-gradient-to-br from-red-500 to-red-700 text-[13px] font-bold text-amber-50 shadow">
            卦
          </span>
          <div className="leading-tight">
            <p className="text-sm font-bold text-stone-100">算卦Lite</p>
            <p className="text-[10px] text-stone-500">管理控制台</p>
          </div>
        </div>

        <nav className="mt-2 flex flex-col gap-0.5 px-2">
          {MENUS.map((m) => (
            <NavLink
              key={m.to}
              to={m.to}
              end={'end' in m ? m.end : false}
              onClick={() => setError('')}
              className={({ isActive }) =>
                `flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-stone-800 font-medium text-amber-300'
                    : 'text-stone-400 hover:bg-stone-800/60 hover:text-stone-200'
                }`
              }
            >
              <i className={`${m.icon} w-4 text-center text-xs`} />
              {m.label}
            </NavLink>
          ))}
        </nav>

        <div className="mt-auto space-y-0.5 px-2 pb-4">
          <NavLink
            to="/"
            className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-xs text-stone-500 transition-colors hover:bg-stone-800/60 hover:text-stone-300"
          >
            <i className="fa-solid fa-arrow-left w-4 text-center" />
            返回用户端
          </NavLink>
          <button
            onClick={() => void adminLogout().then(() => navigate('/admin/login', { replace: true }))}
            className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-xs text-stone-500 transition-colors hover:bg-stone-800/60 hover:text-red-400"
          >
            <i className="fa-solid fa-right-from-bracket w-4 text-center" />
            退出登录
          </button>
        </div>
      </aside>

      {/* 内容区 */}
      <main className="flex-1 overflow-auto p-6">
        {error && (
          <p className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-600">{error}</p>
        )}
        <Outlet context={{ setError }} />
      </main>
    </div>
  );
}
